import { esc } from "./stars";

export interface AutocompleteItem {
  label: string;
  sublabel?: string;
  value: unknown;
}

export interface AutocompleteOptions {
  input: HTMLInputElement;
  source: (query: string) => Promise<AutocompleteItem[]> | AutocompleteItem[];
  onSelect: (item: AutocompleteItem) => void;
  minChars?: number;
  debounceMs?: number;
}

export class Autocomplete {
  private _opts: AutocompleteOptions;
  private _wrapperEl: HTMLDivElement;
  private _dropdownEl: HTMLDivElement;
  private _items: AutocompleteItem[] = [];
  private _highlightIndex = -1;
  private _requestId = 0;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _dismissTimer: ReturnType<typeof setTimeout> | null = null;
  private _isOpen = false;

  private _onInputBound: () => void;
  private _onKeydownBound: (e: KeyboardEvent) => void;
  private _onDocClickBound: (e: MouseEvent) => void;

  constructor(opts: AutocompleteOptions) {
    this._opts = opts;

    // Wrap the input in a relative container so the dropdown scrolls with it
    this._wrapperEl = document.createElement("div");
    this._wrapperEl.className = "autocomplete-wrapper";
    opts.input.parentNode!.insertBefore(this._wrapperEl, opts.input);
    this._wrapperEl.appendChild(opts.input);

    this._dropdownEl = document.createElement("div");
    this._dropdownEl.className = "autocomplete-dropdown";
    this._dropdownEl.setAttribute("role", "listbox");
    this._dropdownEl.dataset.autocompleteFor = opts.input.id || "";
    this._dropdownEl.style.display = "none";
    this._wrapperEl.appendChild(this._dropdownEl);

    this._onInputBound = this._onInput.bind(this);
    this._onKeydownBound = this._onKeydown.bind(this);
    this._onDocClickBound = this._onDocClick.bind(this);

    opts.input.addEventListener("input", this._onInputBound);
    opts.input.addEventListener("keydown", this._onKeydownBound);
    document.addEventListener("mousedown", this._onDocClickBound);
  }

  destroy(): void {
    this._opts.input.removeEventListener("input", this._onInputBound);
    this._opts.input.removeEventListener("keydown", this._onKeydownBound);
    document.removeEventListener("mousedown", this._onDocClickBound);
    this._clearTimers();
    // Unwrap: move input back out and remove the wrapper
    if (this._wrapperEl.parentNode) {
      this._wrapperEl.parentNode.insertBefore(this._opts.input, this._wrapperEl);
      this._wrapperEl.remove();
    }
  }

  // -- Event handlers -------------------------------------------------------

  private _onInput(): void {
    const val = this._opts.input.value.trim();
    const minChars = this._opts.minChars ?? 3;

    if (val.length < minChars) {
      this._close();
      return;
    }

    this._clearTimers();
    const delay = this._opts.debounceMs ?? 0;
    if (delay > 0) {
      this._debounceTimer = setTimeout(() => this._fetch(val), delay);
    } else {
      this._fetch(val);
    }
  }

  private _onKeydown(e: KeyboardEvent): void {
    if (!this._isOpen || this._items.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this._highlightIndex =
          (this._highlightIndex + 1) % this._items.length;
        this._updateHighlight();
        break;

      case "ArrowUp":
        e.preventDefault();
        this._highlightIndex =
          this._highlightIndex <= 0
            ? this._items.length - 1
            : this._highlightIndex - 1;
        this._updateHighlight();
        break;

      case "Enter":
        if (this._highlightIndex >= 0) {
          e.preventDefault();
          this._select(this._highlightIndex);
        }
        break;

      case "Escape":
        e.preventDefault();
        this._close();
        break;
    }
  }

  private _onDocClick(e: MouseEvent): void {
    const target = e.target as Node;
    if (
      !this._dropdownEl.contains(target) &&
      target !== this._opts.input
    ) {
      this._close();
    }
  }

  // -- Core logic -----------------------------------------------------------

  private async _fetch(query: string): Promise<void> {
    const thisId = ++this._requestId;

    this._showMessage("autocomplete-loading", "SCANNING...");

    try {
      const result = this._opts.source(query);
      const items = result instanceof Promise ? await result : result;

      if (thisId !== this._requestId) return; // stale

      if (items.length === 0) {
        this._showMessage("autocomplete-empty", "NO RESULTS FOUND");
        this._dismissTimer = setTimeout(() => this._close(), 2000);
        return;
      }

      this._items = items;
      this._highlightIndex = -1;
      this._renderItems();
    } catch {
      if (thisId !== this._requestId) return;
      this._showMessage("autocomplete-error", "SEARCH UNAVAILABLE");
      this._dismissTimer = setTimeout(() => this._close(), 2000);
    }
  }

  private _renderItems(): void {
    this._dropdownEl.innerHTML = this._items
      .map(
        (item, i) =>
          `<div class="autocomplete-item" role="option" data-index="${i}">
            <span class="autocomplete-item__label">${esc(item.label)}</span>
            ${item.sublabel ? `<span class="autocomplete-item__sublabel">${esc(item.sublabel)}</span>` : ""}
          </div>`,
      )
      .join("");

    this._dropdownEl.addEventListener("click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(
        ".autocomplete-item",
      );
      if (el?.dataset.index != null) {
        this._select(Number(el.dataset.index));
      }
    });

    this._position();
    this._isOpen = true;
  }

  private _select(index: number): void {
    const item = this._items[index];
    if (!item) return;
    this._opts.input.value = item.label;
    this._opts.onSelect(item);
    this._close();
  }

  private _showMessage(className: string, text: string): void {
    this._clearDismissTimer();
    this._dropdownEl.innerHTML = `<div class="${className}">${esc(text)}</div>`;
    this._position();
    this._isOpen = true;
  }

  private _updateHighlight(): void {
    this._dropdownEl
      .querySelectorAll(".autocomplete-item")
      .forEach((el, i) => {
        el.classList.toggle(
          "autocomplete-item--active",
          i === this._highlightIndex,
        );
      });

    const active = this._dropdownEl.querySelector(
      ".autocomplete-item--active",
    );
    active?.scrollIntoView({ block: "nearest" });
  }

  private _position(): void {
    this._dropdownEl.style.display = "block";
  }

  private _close(): void {
    this._isOpen = false;
    this._dropdownEl.style.display = "none";
    this._dropdownEl.innerHTML = "";
    this._items = [];
    this._highlightIndex = -1;
    this._clearTimers();
  }

  private _clearTimers(): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    this._clearDismissTimer();
  }

  private _clearDismissTimer(): void {
    if (this._dismissTimer) {
      clearTimeout(this._dismissTimer);
      this._dismissTimer = null;
    }
  }
}
